import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Loader } from '@resy/ui-kitchen';
import { fetchS3JsonFile, scrollTo, handleMktAnalytics } from '../AmexMarketingUtils/AmexUtils';
import {
  fetchEvents, currentRecurringEvents, fetchPastEvents,
  getFeaturedEvents,
} from './AmexCenturionNYDetails';
import { FOLDER_NAME, DATA_FILE_NAME, RECURRING_EVENTS_FILE_NAME } from './constants';
import { EVENT_CONTEXT, CTA_EVENT_NAME } from '../AmexMarketingUtils/constants';
import AmexTabbedCarousel from '../AmexMarketingUtils/AmexTabbedCarousel';
import AmexCenturionBanner from './AmexCenturionBanner';
import AmexCenturionNYUpcomingEvents from './AmexCenturionNYUpcomingEvents';
import AmexCenturionNYRecurringEvents from './AmexCenturionNYRecurringEvents';
import AmexCenturionNYEventCard from './AmexCenturionNYEventCard';
import AmexTerms from '../AmexMarketingUtils/AmexTerms';
import AmexFooter from '../AmexMarketingUtils/AmexFooter';
import './AmexCenturionNY.scss';
import './AmexCenturionNYEventCard/AmexCenturionNYEventCard.scss';

let hashTimeout = null;

class AmexCenturionNY extends Component {
  static propTypes = {
    $rootScope: PropTypes.shape({
      matchMedia: PropTypes.shape({
        xs: PropTypes.bool,
        sm: PropTypes.bool,
        md: PropTypes.bool,
        lg: PropTypes.bool,
        ipadLandscape: PropTypes.bool,
        xl: PropTypes.bool,
      }).isRequired,
      $digest: PropTypes.func,
    }).isRequired,
    Analytics: PropTypes.shape({
      event: PropTypes.func,
    }).isRequired,
    ResyAmexConfig: PropTypes.shape({
      baseRequestUrl: PropTypes.string.isRequired,
    }).isRequired,
    $location: PropTypes.shape({
      path: PropTypes.func,
    }).isRequired,
  };

  state = {
    eventsJSONData: null,
    hash: null,
    recurringJSONEvents: [],
    isPageReady: false,
  };

  componentDidMount() {
    const currentHash = (window.location.hash).replace('#', '');
    window.addEventListener('hashchange', this.handleHashChange, false);
    this.fetchDataAndAPIs(currentHash);
  }

  // scrollTo moved to fetchDataAndAPIs after all API calls

  componentWillUnmount() {
    window.removeEventListener('hashchange', this.handleHashChange);
    if (hashTimeout) clearTimeout(hashTimeout);
  }

  handleHashChange = () => {
    const currentLocationHash = (window.location.hash).replace('#', '');
    if (currentLocationHash && currentLocationHash !== '') {
      this.setState({ hash: currentLocationHash });
    }
  }

  handleHashUpdate = async (hash) => {
    hashTimeout = setTimeout(() => scrollTo(`#${hash}`), 0);
    this.setState({ isPageReady: true });
  }

  fetchDataAndAPIs = async (hash) => {
    const {
      ResyAmexConfig: {
        baseRequestUrl,
      },
    } = this.props;

    const eventsJSONData = await fetchS3JsonFile(baseRequestUrl, FOLDER_NAME, DATA_FILE_NAME);
    const isPageEnabled = eventsJSONData?.is_page_enabled;
    const hideRecurringEvents = eventsJSONData?.event_details?.recurring?.hide_section;
    const hideUpcomingEvents = eventsJSONData?.event_details?.upcoming?.hide_section;
    const hideFeaturedEvent = eventsJSONData?.event_details?.featured?.hide_section;

    this.setState({ eventsJSONData, hash });
    if (!isPageEnabled) return;

    const {
      venue_id: venueId,
      location: locSlug,
      venue_slug: urlSlug,
      is_events_non_caching_enabled: isEventsNonCachingEnabled,
    } = eventsJSONData;

    const venue = {
      location: {
        urlSlug: locSlug,
      },
      urlSlug,
    };

    // UPCOMING EVENTS AND FEATURED EVENT
    if (venueId && (!hideUpcomingEvents || !hideFeaturedEvent)) {
      const isSoldOutUpcomingEnabled = eventsJSONData?.event_details?.upcoming?.is_sold_out_enabled;
      const previewSearchValue = eventsJSONData?.event_details?.featured?.preview_search_value;
      const upcomingAPIEvents = await fetchEvents(venueId, isSoldOutUpcomingEnabled, venue);
      this.setState({ venue });

      if (!hideUpcomingEvents) {
        this.setState({
          upcomingAPIEvents,
        });
      }

      if (!hideFeaturedEvent && upcomingAPIEvents) {
        this.setState({
          featuredEvent: getFeaturedEvents(upcomingAPIEvents, previewSearchValue),
        });
      }
    }

    // RECURRING EVENTS
    if (!hideRecurringEvents) await this.fetchRecurringEvents(baseRequestUrl, isEventsNonCachingEnabled);

    // PAST EVENTS
    const eventsToCheckForPast = eventsJSONData?.carousel?.sections.filter((sectionItem) => sectionItem.id === 'PastEvents');
    if (eventsToCheckForPast) {
      const pastEvents = await fetchPastEvents(eventsToCheckForPast[0]?.content, venue);
      this.setState({ pastEvents });
    }
    await this.handleHashUpdate(hash);
  }

  fetchRecurringEvents = async (baseRequestUrl, isEventsNonCachingEnabled) => {
    const recurringEventsData = await fetchS3JsonFile(baseRequestUrl, FOLDER_NAME, RECURRING_EVENTS_FILE_NAME, isEventsNonCachingEnabled);
    const events = recurringEventsData?.events;
    if (events?.length > 0) {
      const recurringJSONEvents = currentRecurringEvents(events);
      this.setState({ recurringJSONEvents });
    }
    return null;
  }

  handleTagging = (extraParams) => {
    const { Analytics: { event } } = this.props;
    handleMktAnalytics(event, CTA_EVENT_NAME, extraParams);
  }

  renderFeaturedEvent = () => {
    const {
      featuredEvent, venue, eventsJSONData,
    } = this.state;

    if (!featuredEvent || !venue || !eventsJSONData?.event_details || !eventsJSONData?.event_types) return null;

    const {
      event_types: eventButtons,
      event_details: {
        featured: {
          title,
        },
      },
      event_details: eventDetails,
    } = eventsJSONData;

    const {
      ResyAmexConfig: {
        baseRequestUrl,
      },
      Analytics,
    } = this.props;

    return (
      <section className="AmexCenturionNYFeaturedEvent">
        <div className="container">
          <h2 className="AmexCenturionNYFeaturedEvent__title featured">{title}</h2>
          <div className="layout-single featured">
            <AmexCenturionNYEventCard
              {...featuredEvent}
              key={featuredEvent.id}
              eventButtons={eventButtons}
              baseRequestUrl={baseRequestUrl}
              venue={{ ...venue }}
              isFeaturedEvent
              eventDetails={eventDetails?.featured}
              Analytics={Analytics}
            />
          </div>
        </div>
      </section>
    );
  }

  render() {
    const {
      ResyAmexConfig: {
        baseRequestUrl,
      },
      $location,
      $rootScope,
      Analytics,
    } = this.props;

    const {
      eventsJSONData,
      hash,
      upcomingAPIEvents,
      venue,
      recurringJSONEvents,
      pastEvents,
      isPageReady,
    } = this.state;

    if (!eventsJSONData) return null;

    const {
      is_page_enabled: isPageEnabled = false,
      loading_aria: loadingAria,
      banner: bannerConfig,
      carousel: carouselConfig,
      above_terms: aboveTermsConfig,
      terms: termConfig,
      footer: footerConfig,
      aws_folder_name: awsFolderName,
      event_types: eventButtons,
      event_details: eventDetails,
    } = eventsJSONData;

    const renderLoadingEvents = (
      <div role="alert" aria-busy="true" aria-label={loadingAria}>
        <Loader className="AmexCenturionNY__loader" />
      </div>
    );

    if (!isPageEnabled) {
      // 404 path
      $location.path('/404');
      $rootScope.$digest();
    }

    const hideUpcomingEvents = eventDetails?.upcoming?.hide_section;
    const hideRecurringEvents = eventDetails?.recurring?.hide_section || recurringJSONEvents.length === 0;
    const hideCarousel = carouselConfig?.hide_section || carouselConfig?.sections?.length === 0;

    return (
      <div className="AmexCenturionNY">
        <AmexCenturionBanner
          awsFolderName={awsFolderName}
          baseRequestUrl={baseRequestUrl}
          bannerConfig={bannerConfig}
          Analytics={Analytics}
          matchMedia={$rootScope.matchMedia}
        />

        {this.renderFeaturedEvent()}
        {(!hideUpcomingEvents && !upcomingAPIEvents) && renderLoadingEvents}
        {(!hideUpcomingEvents && upcomingAPIEvents) && (
          <AmexCenturionNYUpcomingEvents
            baseRequestUrl={baseRequestUrl}
            events={upcomingAPIEvents}
            venue={venue}
            eventButtons={eventButtons}
            eventDetails={eventDetails?.upcoming}
            matchMedia={$rootScope.matchMedia}
            Analytics={Analytics}
          />
        )}
        {!hideRecurringEvents && (
          <AmexCenturionNYRecurringEvents
            awsFolderName={awsFolderName}
            baseRequestUrl={baseRequestUrl}
            eventButtons={eventButtons}
            eventDetails={eventDetails}
            recurringJSONEvents={recurringJSONEvents}
            matchMedia={$rootScope.matchMedia}
            handleTagging={this.handleTagging}
          />
        )}
        <div className="AmexCenturionNY__bottom">
          {!hideCarousel && (
            <AmexTabbedCarousel
              awsFolderName={awsFolderName}
              baseRequestUrl={baseRequestUrl}
              carouselConfig={carouselConfig}
              handleTagging={this.handleTagging}
              Analytics={Analytics}
              hash={hash}
              matchMedia={$rootScope.matchMedia}
              eventContents={pastEvents}
              venueContent={venue}
              eventContext={EVENT_CONTEXT.PAST_EVENTS}
              isPageReady={isPageReady}
              hasStickyHeader={false}
            />
          )}
          <AmexTerms
            baseRequestUrl={baseRequestUrl}
            hash={hash}
            termsConfig={termConfig}
            aboveTermsConfig={aboveTermsConfig}
            Analytics={Analytics}
          />
          <AmexFooter
            footerConfig={footerConfig}
            baseRequestUrl={baseRequestUrl}
            Analytics={Analytics}
            awsFolderName={awsFolderName}
          />
        </div>
      </div>
    );
  }
}

export default AmexCenturionNY;
